import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { X, HeartPulse, ShieldAlert, Zap, Brain, Loader2, Activity } from "lucide-react";
import { LineChart, Line, YAxis, ResponsiveContainer } from "recharts";

const Bar = ({ label, value, color }) => (
  <div>
    <div className="mb-1 flex items-center justify-between text-[11px]">
      <span className="text-white/50">{label}</span>
      <span className="font-mono font-semibold" style={{ color }}>{value}%</span>
    </div>
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, value)}%`, background: color }} />
    </div>
  </div>
);

// Global player fitness card — opened by pitch clicks OR the voice agent (window 'octon:show-player').
export const GlobalPlayerFitness = () => {
  const [ids, setIds] = useState(null);   // {teamId, playerId}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = async (e) => {
      const d = e.detail || {};
      if (d.teamId && d.playerId) { setIds({ teamId: d.teamId, playerId: d.playerId }); return; }
      if (d.team && d.player) {
        try {
          const r = await api.get(`/players/find?team=${encodeURIComponent(d.team)}&name=${encodeURIComponent(d.player)}`);
          setIds({ teamId: r.data.team_id, playerId: r.data.player_id });
        } catch (_) {}
      }
    };
    window.addEventListener("octon:show-player", handler);
    return () => window.removeEventListener("octon:show-player", handler);
  }, []);

  useEffect(() => {
    if (!ids) { setData(null); return; }
    setLoading(true);
    api.get(`/players/dossier?team_id=${ids.teamId}&player_id=${ids.playerId}`)
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [ids]);

  const close = () => { setIds(null); setData(null); };
  // Hands-free: the voice agent (close_card) or the Escape key closes this card.
  useEffect(() => {
    if (!ids) return;
    const onClose = () => close();
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("octon:close-overlays", onClose);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("octon:close-overlays", onClose); window.removeEventListener("keydown", onKey); };
  }, [ids]);
  const p = data?.player;
  const inj = data?.injury_risk;
  const mind = p?.mindset || {};
  const acwr = data?.acwr;
  const energy = (data?.forecast || []).map((f, i) => ({ i, e: f.readiness }));
  const mentalCap = mind?.resonance?.score;

  return (
    <AnimatePresence>
      {ids && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={close} data-testid="player-fitness-modal"
          data-octon-overlay="player fitness card readiness mind body" data-octon-label={p ? `Player card: ${p.name}` : "Player card"}
          data-octon-state={loading || !p ? "loading" : "ready"}>
          <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl border border-white/12 bg-[#0b0b0e] p-6 shadow-2xl">
            {loading || !p ? (
              <div className="flex h-64 items-center justify-center text-white/50">
                <Loader2 className="animate-spin" /> <span className="ml-2 text-sm">Reading fitness…</span>
              </div>
            ) : (
              <>
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <p className="font-tac text-[9px] tracking-widest txt-cyan">MIND &amp; BODY FITNESS</p>
                    <h3 className="font-head text-2xl font-black tracking-tight">{p.name}</h3>
                    <p className="text-xs text-white/45">{p.position_detail || p.position} · age {p.factors?.age} · rating {p.rating ?? "—"} · {p.status}</p>
                  </div>
                  <button onClick={close} data-testid="close-fitness" className="text-white/40 hover:text-white"><X size={20} /></button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3 rounded-xl border border-white/8 bg-white/[0.02] p-4">
                    <div className="flex items-center gap-1.5 text-[10px] font-tac tracking-widest text-white/40"><HeartPulse size={12} style={{ color: "#33d17a" }} /> BODY</div>
                    <Bar label="Readiness" value={p.readiness} color="#33d17a" />
                    <Bar label="Freshness / energy" value={p.factors?.freshness ?? 0} color="#00e5ff" />
                    <div className="flex items-center justify-between text-[11px]"><span className="flex items-center gap-1 text-white/50"><ShieldAlert size={12} style={{ color: inj?.danger ? "#ff3b30" : "#ffb800" }} /> Injury risk</span><span className="font-mono" style={{ color: inj?.danger ? "#ff3b30" : "#ffb800" }}>{inj?.pct}% · {inj?.level}</span></div>
                    <div className="flex items-center justify-between text-[11px]"><span className="flex items-center gap-1 text-white/50"><Activity size={12} /> ACWR</span><span className="font-mono text-white/80">{acwr?.ratio} · {acwr?.status}</span></div>
                  </div>
                  <div className="space-y-3 rounded-xl border border-white/8 bg-white/[0.02] p-4">
                    <div className="flex items-center gap-1.5 text-[10px] font-tac tracking-widest text-white/40"><Brain size={12} style={{ color: "#ff6ad5" }} /> MIND</div>
                    <Bar label={`Morale · ${mind.state || ""}`} value={mind.morale ?? 0} color="#ff6ad5" />
                    <Bar label="Mental capacity (resonance)" value={mentalCap ?? 0} color="#a78bfa" />
                    <p className="text-[10px] leading-snug text-white/45">{(mind.drivers || []).join(" · ")}</p>
                  </div>
                </div>

                {energy.length > 0 && (
                  <div className="mt-4 rounded-xl border border-white/8 bg-black/30 p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-[9px] font-tac tracking-widest text-white/40"><Zap size={12} style={{ color: "#ccff00" }} /> PREDICTED ENERGY · NEXT 4 STARTS</div>
                    <div className="h-14"><ResponsiveContainer width="100%" height="100%"><LineChart data={energy}><YAxis domain={[0, 100]} hide /><Line type="monotone" dataKey="e" stroke="#ccff00" strokeWidth={2} dot={{ r: 2 }} /></LineChart></ResponsiveContainer></div>
                  </div>
                )}

                {data?.recommendation && (
                  <div className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3">
                    <p className="text-[9px] font-tac tracking-widest text-cyan-300">OCTON CALL · {data.recommendation.tag}</p>
                    <p className="mt-1 text-sm text-white/85">{data.recommendation.call} — {data.recommendation.note}</p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
